package dev.akshaychordiya.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.components.layouts.PageLayoutData
import dev.akshaychordiya.components.layouts.formatDate
import dev.akshaychordiya.components.widgets.ListRowStyle
import dev.akshaychordiya.components.widgets.PageHeader
import dev.akshaychordiya.components.widgets.Preview
import dev.akshaychordiya.components.widgets.Subscribe
import dev.akshaychordiya.generated.ArticleEntry
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.generated.articleIndex
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@InitRoute
fun initArticlesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Articles"))
}

/** Lead image above the text on a phone, beside it once there's room. */
val ArticleLayoutStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.cssRem)
    }
    Breakpoint.SM { Modifier.flexDirection(FlexDirection.Row) }
}

val ArticleTitleStyle = CssStyle {
    base { Modifier.fontSize(1.2.cssRem).fontWeight(600).lineHeight(1.35).smoothTransition("color") }
    hover { Modifier.color(colorMode.toSitePalette().brandPrimary) }
}

/** The RSS pill at the bottom of the index. */
val RssPillStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.85.cssRem)
            .color(colorMode.toSitePalette().subdued)
            .padding(topBottom = 0.4.cssRem, leftRight = 0.9.cssRem)
            .borderRadius(2.cssRem)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("color", "border-color", "background-color")
    }
    hover {
        Modifier
            .color(colorMode.toSitePalette().brandPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary)
            .backgroundColor(colorMode.toSitePalette().surface)
    }
}

@Composable
private fun ArticleRow(article: ArticleEntry, index: Int) {
    val palette = ColorMode.current.toSitePalette()

    Div(ListRowStyle.toModifier().fadeUp(index).toAttrs()) {
        Link(
            article.route,
            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
        ) {
            Div(ArticleLayoutStyle.toAttrs()) {
                if (article.image.isNotEmpty()) {
                    Preview(article.image, "Lead image for ${article.title}")
                }
                Column(Modifier.fillMaxWidth().gap(0.35.cssRem)) {
                    SpanText(formatDate(article.date), MetaTextStyle.toModifier())
                    SpanText(article.title, ArticleTitleStyle.toModifier())
                    if (article.description.isNotEmpty()) {
                        SpanText(article.description, Modifier.fontSize(0.98.cssRem).color(palette.subdued))
                    }
                }
            }
        }
    }
}

/**
 * The search field.
 *
 * Filtering happens in the browser over the generated index, which is already in the bundle - there is no endpoint to
 * call and nothing to keep in sync. With ten articles that is trivially fast; if this ever grows past a few hundred
 * it would be worth pre-building a search index instead.
 */
val SearchInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.65.cssRem, leftRight = 0.9.cssRem)
            .borderRadius(0.5.cssRem)
            .fontSize(1.cssRem)
            .backgroundColor(colorMode.toSitePalette().background)
            .color(colorMode.toSitePalette().text)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("border-color")
    }
    focusVisible { Modifier.border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary) }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun ArticlesPage() {
    val palette = ColorMode.current.toSitePalette()

    PageHeader(
        eyebrow = "Writing",
        title = "Articles",
        subtitle = "Android and Kotlin, written up properly. New pieces land here first.",
    )

    Subscribe()

    var query by remember { mutableStateOf("") }
    val needle = query.trim().lowercase()
    // Title and description only. Full text isn't in the bundle - the articles are separate pages - so searching it
    // would mean shipping every word of every post to every visitor.
    val matches = if (needle.isEmpty()) {
        articleIndex
    } else {
        articleIndex.filter {
            it.title.lowercase().contains(needle) || it.description.lowercase().contains(needle)
        }
    }

    Div(attrs = Modifier.fillMaxWidth().margin(top = 2.5.cssRem).toAttrs()) {
        Input(
            type = InputType.Search,
            attrs = SearchInputStyle.toModifier().toAttrs {
                attr("placeholder", "Search articles")
                attr("aria-label", "Search articles")
                attr("value", query)
                onInput { query = it.value }
            },
        )
    }

    Column(Modifier.fillMaxWidth().margin(top = 1.5.cssRem)) {
        matches.forEachIndexed { index, article -> ArticleRow(article, index) }
    }

    if (matches.isEmpty()) {
        SpanText(
            "Nothing matches \"${query.trim()}\". Try a different word, or clear the box to see all " +
                "${articleIndex.size} articles.",
            Modifier.margin(top = 1.cssRem).fontSize(0.95.cssRem).color(palette.subdued),
        )
    }
}
